/**
 * TablePaymentDialog — ResponsivePaymentSheet extracted from TableDialogs.
 * Receives all payment-related state and handlers as props.
 */
import { useTranslation } from "react-i18next";
import { formatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Receipt, XCircle } from "lucide-react";
import { ResponsivePaymentSheet } from "@/components/ui/responsive-payment-sheet";

export function TablePaymentDialog({
  showPayment, resetPaymentState,
  paymentMode, setPaymentMode, partialPayments, handleRemovePartialPayment,
  remainingToPay, finalTotal, totalPartialPaid, appliedDiscount,
  currentPartialAmount, setCurrentPartialAmount,
  paymentMethod, setPaymentMethod, cardType, setCardType,
  cashReceived, setCashReceived, needsChange, setNeedsChange, changeAmount, partialCashChange,
  handleAddPartialPayment, processingPayment, handlePayment,
}) {
  const { t } = useTranslation();
  const partialAmount = parseFloat(currentPartialAmount) || 0;

  return (
    <ResponsivePaymentSheet
      open={showPayment}
      onOpenChange={(open) => { if (!open) resetPaymentState(); }}
      title={<><Receipt className="w-5 h-5 text-emerald-600 inline mr-2" /> {t("tables.registerPayment", "Registrar Pago")}</>}
      footer={
        <div className="flex gap-2 w-full justify-end">
          <Button variant="outline" onClick={resetPaymentState}>{t("common.cancel", "Cancelar")}</Button>
          {paymentMode === "combined" && remainingToPay > 0 && (
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold gap-2"
              onClick={handleAddPartialPayment}
              disabled={!paymentMethod || !currentPartialAmount || partialAmount <= 0 || (paymentMethod === "card" && !cardType) || (paymentMethod === "cash" && needsChange && (!cashReceived || parseFloat(cashReceived) < partialAmount))}
              data-testid="add-partial-btn"
            >
              + {t("tables.addPayment", "Añadir Pago")}
            </Button>
          )}
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2"
            onClick={handlePayment}
            disabled={
              processingPayment ||
              (paymentMode === "single" && (!paymentMethod || (paymentMethod === "card" && !cardType) || (paymentMethod === "cash" && needsChange && (!cashReceived || parseFloat(cashReceived) < finalTotal)))) ||
              (paymentMode === "combined" && (partialPayments.length === 0 || remainingToPay > 0.01))
            }
            data-testid="confirm-payment-btn"
          >
            {processingPayment ? t("common.processing", "Procesando...") :
             paymentMode === "single" ? `${t("common.confirm", "Confirmar")} ${formatPrice(finalTotal)}` :
             `${t("common.confirm", "Confirmar")} ${partialPayments.length} ${t("tables.payments", "pagos")}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">{t("tables.totalAccount", "Total Cuenta")}</p>
            <p className="text-xl font-black text-gray-900">{formatPrice(finalTotal)}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs text-emerald-600">{t("tables.paid", "Pagado")}</p>
            <p className="text-xl font-black text-emerald-700">{formatPrice(totalPartialPaid)}</p>
          </div>
          <div className={`rounded-lg p-3 ${remainingToPay > 0 ? "bg-amber-50" : "bg-emerald-100"}`}>
            <p className="text-xs text-gray-600">{t("tables.pending", "Pendiente")}</p>
            <p className={`text-xl font-black ${remainingToPay > 0 ? "text-amber-700" : "text-emerald-700"}`}>{formatPrice(remainingToPay)}</p>
          </div>
        </div>
        {appliedDiscount > 0 && (
          <p className="text-center text-sm text-amber-600">{t("tables.discountApplied", "Descuento")}: -{formatPrice(appliedDiscount)}</p>
        )}
        <div className="flex gap-2">
          <button onClick={() => { setPaymentMode("single"); }} className={`flex-1 p-3 rounded-lg border-2 text-sm font-bold transition ${paymentMode === "single" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 hover:border-gray-300"}`} data-testid="payment-mode-single">{t("tables.singlePayment", "Pago Único")}</button>
          <button onClick={() => setPaymentMode("combined")} className={`flex-1 p-3 rounded-lg border-2 text-sm font-bold transition ${paymentMode === "combined" ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 hover:border-gray-300"}`} data-testid="payment-mode-combined">{t("tables.combinedPayment", "Pago Combinado")}</button>
        </div>
        {paymentMode === "combined" && partialPayments.length > 0 && (
          <div className="bg-purple-50 rounded-lg p-3 space-y-2">
            <p className="text-xs font-bold text-purple-700 uppercase">{t("tables.addedPayments", "Pagos Añadidos")}</p>
            {partialPayments.map((p, idx) => (
              <div key={p.id} className="flex items-center justify-between bg-white rounded px-3 py-2 text-sm">
                <span className="font-medium">
                  {p.method === "cash" ? "💵 " : p.method === "card" ? "💳 " : "📱 "}
                  {p.method === "cash" ? t("tables.cash", "Efectivo") : p.method === "card" ? `${t("tables.card", "Tarjeta")} (${p.card_type})` : p.method.toUpperCase()}
                </span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-emerald-700">{formatPrice(p.amount)}</span>
                  {p.change_amount > 0 && <span className="text-xs text-gray-500">({t("tables.change", "Cambio")}: {formatPrice(p.change_amount)})</span>}
                  <button onClick={() => handleRemovePartialPayment(p.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`remove-partial-${idx}`}>
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {(paymentMode === "single" || remainingToPay > 0) && (
          <>
            {paymentMode === "combined" && (
              <div className="space-y-2">
                <Label className="font-bold">{t("tables.partialAmount", "Monto Parcial")} (€)</Label>
                <Input type="number" step="0.01" min="0.01" max={remainingToPay} value={currentPartialAmount} onChange={e => setCurrentPartialAmount(e.target.value)} className="text-lg font-bold text-center" placeholder={remainingToPay.toFixed(2)} data-testid="partial-amount-input" />
                <div className="flex gap-2">
                  {[remainingToPay, Math.ceil(remainingToPay / 2 * 100) / 100, 10, 20, 50].filter((v, i, a) => v <= remainingToPay && a.indexOf(v) === i).slice(0, 4).map(amt => (
                    <button key={amt} onClick={() => setCurrentPartialAmount(amt.toFixed(2))} className="flex-1 py-1 px-2 text-xs font-bold border rounded hover:bg-gray-50">{formatPrice(amt)}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="font-bold">{t("tables.paymentMethod", "Forma de Pago")}</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "cash", label: t("tables.cash", "Efectivo"), icon: "💵" },
                  { key: "card", label: t("tables.card", "Tarjeta"), icon: "💳" },
                  { key: "debit", label: t("tables.debit", "Débito"), icon: "💳" },
                  { key: "credit", label: t("tables.credit", "Crédito"), icon: "💳" },
                  { key: "mbway", label: "MBWay", icon: "📱" },
                  { key: "bizum", label: "Bizum", icon: "📱" },
                ].map(m => (
                  <button key={m.key} onClick={() => { setPaymentMethod(m.key); if (m.key !== "card") setCardType(""); }} className={`p-2 rounded-lg border-2 text-xs font-bold transition ${paymentMethod === m.key ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gray-200 hover:border-gray-300"}`} data-testid={`payment-method-${m.key}`}>
                    <span className="mr-1">{m.icon}</span>{m.label}
                  </button>
                ))}
              </div>
            </div>
            {paymentMethod === "cash" && (
              <div className="space-y-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-3">
                <button
                  type="button"
                  onClick={() => { setNeedsChange(!needsChange); setCashReceived(""); }}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border-2 font-semibold text-sm transition ${
                    needsChange ? "border-amber-400 bg-amber-100 text-amber-800" : "border-emerald-400 bg-emerald-50 text-emerald-700"
                  }`}
                  data-testid="toggle-needs-change"
                >
                  <span>{needsChange ? t("tables.needsChange", "Precisa de troco") : t("tables.exactPayment", "Pagamento exato (sem troco)")}</span>
                  <span className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${needsChange ? "bg-amber-400" : "bg-emerald-400"}`}>
                    <span className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${needsChange ? "translate-x-5" : "translate-x-0"}`} />
                  </span>
                </button>
                {needsChange && (
                  <>
                    <div>
                      <Label className="font-bold text-amber-800">{t("tables.receivedAmount", "Valor Recibido")} (€)</Label>
                      <Input type="number" step="0.01" value={cashReceived} onChange={e => setCashReceived(e.target.value)} className="mt-1 text-lg font-bold text-center" placeholder={(paymentMode === "single" ? finalTotal : partialAmount || remainingToPay).toFixed(2)} autoFocus data-testid="cash-received-input" />
                    </div>
                    {cashReceived && parseFloat(cashReceived) >= (paymentMode === "single" ? finalTotal : partialAmount) && (
                      <div className="text-center bg-white rounded-lg p-2">
                        <p className="text-xs text-gray-600">{t("tables.change", "Cambio")}</p>
                        <p className="text-xl font-black text-emerald-700">{formatPrice(paymentMode === "single" ? changeAmount : partialCashChange)}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {paymentMethod === "card" && (
              <div className="space-y-2">
                <Label className="font-bold">{t("tables.cardType", "Tipo de Tarjeta")}</Label>
                <div className="grid grid-cols-3 gap-2">
                  {["Visa", "Mastercard", "Multibanco", "AmEx", "Cabal", t("tables.other", "Otro")].map(type => (
                    <button key={type} onClick={() => setCardType(type)} className={`p-2 rounded-lg border text-xs font-bold transition ${cardType === type ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 hover:border-gray-300"}`} data-testid={`card-type-${type.toLowerCase()}`}>{type}</button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {paymentMode === "combined" && remainingToPay <= 0.01 && partialPayments.length > 0 && (
          <div className="bg-emerald-100 rounded-lg p-4 text-center">
            <p className="text-emerald-800 font-bold">✓ {t("tables.paymentComplete", "Pago completo!")}</p>
            <p className="text-sm text-emerald-600">{t("tables.clickConfirm", "Haga clic en Confirmar para registrar los pagos")}</p>
          </div>
        )}
      </div>
    </ResponsivePaymentSheet>
  );
}
